import React, { useEffect, useMemo, useState, useRef } from "react";
import { API_BASE } from "../api";

/**
 * AIAIAI Dashboard
 * - Fullscreen layout (100vw x 100vh), no scroll
 * - Left: title, profile photo frame, participant-id input (ENTER to load), participant name, logos placeholder
 * - Right: 5 photo frames, graph placeholder, waveform placeholder, big live portrait video frame
 * - Bottom: rainbow bar with a blue downward arrow at random X containing a random score [-99..99]
 *
 * Data flow on valid ID + Enter:
 *  - Load participant from GET ${API_BASE}/deelnemers (client-side filter by id)
 *  - Set profile photo (if available)
 *  - Set live portrait video src to ${API_BASE}/liveportraits/lp--<id>.mp4 (autoplay)
 *  - Background: GET ${API_BASE}/photos?deelnemer_id=<id>; choose 5 random photos; fill frames
 *  - Blue arrow: random x-position and random score [-99..99]
 */

const styles = {
  page: {
    width: "100vw",
    height: "100dvh",
    overflow: "hidden",
    display: "grid",
    gridTemplateColumns: "28% 2px 1fr",
    gridTemplateRows: "1fr",
    background: "#fff",
    color: "#000",
  },
  leftCol: {
    gridColumn: "1 / 2",
    padding: "24px",
    display: "grid",
    gridTemplateRows: "1fr 4fr",
    rowGap: "16px",
    boxSizing: "border-box",
    minHeight: "0",
  },
  divider: {
    gridColumn: "2 / 3",
    background: "#000",
    height: "100%",
    width: "100%",
  },
  rightCol: {
    gridColumn: "3 / 4",
    padding: "24px",
    display: "grid",
    gridTemplateRows: "1fr 3fr 1fr",
    rowGap: "16px",
    boxSizing: "border-box",
    minHeight: "0",
  },
  title: {
    fontSize: "96px",
    fontWeight: 800,
    letterSpacing: "2px",
    color: "#441111",
  },
  subtitle: {
    fontSize: "24px",
    fontWeight: 400,
    marginTop: "0px",
    lineHeight: 1,
    color: "#000",
  },
  frame: {
    background: "#eee",
    border: "4px solid #000",
    borderRadius: "2px",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#000",
    userSelect: "none",
  },
  profileBlock: {
    display: "grid",
    gridTemplateRows: "auto auto 1fr auto",
    rowGap: "16px",
    alignItems: "start",
  },
  profileFrame: {
    width: "100%",
    aspectRatio: "1 / 1",
    position: "relative",
    marginBottom: "8px",
  },
  profileImg: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  idInput: {
    width: "100%",
    fontSize: "72px",
    textAlign: "center",
    padding: "8px 12px",
    borderRadius: "8px",
    border: "4px solid #000",
    outline: "none",
  },
  nameStrip: {
    height: "56px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 600,
  },
  nameRow: {
    display: "grid",
    gridTemplateColumns: "4fr 1fr",
    gridAutoFlow: "column",
    alignItems: "center",
    gap: "8px",
    width: "100%",
    boxSizing: "border-box",
  },
  nameInline: {
    fontSize: "24px",
    fontWeight: 600,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
  },
  idInputInline: {
    fontSize: "24px",
    width: "100%",
    padding: "8px 12px",
    borderRadius: "8px",
    border: "4px solid #000",
    boxSizing: "border-box",
    textAlign: "center",
    minWidth: 0,
  },
  logosPlaceholder: {
    display: "flex",
    flexDirection: "column",
    alignItems: "stretch",
    justifyContent: "flex-start",
    background: "#f6f6f6",
    gap: "8px",
    padding: "0",
    marginTop: "32px",
  },
  yonderWrap: {
    paddingTop: "20px",
    paddingBottom: "20px",
    paddingLeft: "60px",
    paddingRight: "60px",
    boxSizing: "border-box",
    width: "100%",
  },
  logoYonder: {
    width: "100%",
    height: "auto",
    display: "block",
  },
  logoMindlabs: {
    width: "100%",
    height: "auto",
    display: "block",
  },
  photosRow: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: "16px",
  },
  photoCell: {
    position: "relative",
    width: "100%",
    paddingTop: "100%", // square
    overflow: "hidden",
    border: "4px solid #000",
    background: "#eee",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#000",
    boxSizing: "border-box",
  },
  photoImg: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  photoPlaceholder: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  middleArea: {
    display: "grid",
    gridTemplateColumns: "2fr 1fr 2fr",
    gap: "16px",
    alignItems: "stretch",
    minHeight: "0",
  },
  middleColWrap: {
    display: "grid",
    gridTemplateRows: "auto 1fr",
    gap: "8px",
    minHeight: "0",
  },
  smallTitle: {
    fontSize: "18px",
    fontWeight: 700,
    textAlign: "center",
  },
  bigVideoFrame: {
    position: "relative",
    width: "100%",
    height: "100%",
    background: "#eee",
    border: "4px solid #000",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "160px",
    overflow: "hidden",
  },
  video: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  bottomBarWrap: {
    position: "relative",
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
  },
  barGroup: {
    position: "relative",
    width: "100%",
    height: "120px",
  },
  rainbowBar: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: "28px",
    borderRadius: "0",
    background:
      "linear-gradient(90deg, #0aa84f, #7bd147, #ffd740, #ff8f3a, #f44336)",
    border: "none",
  },
  arrowContainer: {
    position: "absolute",
    bottom: "40px", // a bit above the rainbow bar
    transform: "translate(-50%, 0)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
  },
  arrowText: {
    position: "absolute",
    left: "50%",
    top: "16px",
    transform: "translate(-50%, 0)",
    color: "#000",
    fontWeight: 700,
    fontSize: "32px",
    lineHeight: 1,
  },
  smallNote: {
    fontSize: "14px",
    color: "#666",
    marginTop: "4px",
  },
};

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function AIAIAIDashboard() {
  useEffect(() => {
    document.title = "AIAIAI Dashboard";
  }, []);

  const [idInput, setIdInput] = useState("");
  const [participant, setParticipant] = useState(null);
  const [profileUrl, setProfileUrl] = useState(null);

  const [photoUrls, setPhotoUrls] = useState([]); // up to 5
  const [videoSrc, setVideoSrc] = useState(null);
  const [videoError, setVideoError] = useState(false);
  const [sideVideoSrc, setSideVideoSrc] = useState(null);
  const [sideVideoError, setSideVideoError] = useState(false);

  const [randomScore, setRandomScore] = useState(0);
  const [arrowLeftPct, setArrowLeftPct] = useState(50);

  const [status, setStatus] = useState("");
  const [captchaScores, setCaptchaScores] = useState({ good: 0, missed: 0, wrong: 0 });

  // ref + measured height for the chart inner area (to compute pixel heights)
  const chartAreaRef = useRef(null);
  const [chartInnerHeight, setChartInnerHeight] = useState(0);

  const isValidId = /^\d{3}$/.test(idInput);

  // measure chart inner height on mount, on participant change and on resize
  useEffect(() => {
    function updateHeight() {
      try {
        const el = chartAreaRef.current;
        if (el && el.clientHeight) {
          // inner chart area measured directly
          setChartInnerHeight(el.clientHeight);
        } else {
          // fallback: try parent height (bigVideoFrame)
          const parent = el ? el.parentElement : null;
          if (parent && parent.clientHeight) setChartInnerHeight(Math.round(parent.clientHeight * 0.7));
        }
      } catch (e) {
        setChartInnerHeight(0);
      }
    }
    updateHeight();
    const ro = new ResizeObserver(updateHeight);
    if (chartAreaRef.current) ro.observe(chartAreaRef.current);
    window.addEventListener("resize", updateHeight);
    return () => {
      try {
        if (chartAreaRef.current) ro.unobserve(chartAreaRef.current);
      } catch (e) {}
      window.removeEventListener("resize", updateHeight);
    };
  }, [participant]);

  const handleIdChange = (e) => {
    const digits = String(e.target.value || "").replace(/\D/g, "").slice(0, 3);
    setIdInput(digits);
    if (digits.length === 0) {
      resetView();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && isValidId) {
      loadParticipant(idInput);
    }
  };

  const resetView = () => {
    setParticipant(null);
    setProfileUrl(null);
    setPhotoUrls([]);
    setVideoSrc(null);
    setVideoError(false);
    setSideVideoSrc(null);
    setSideVideoError(false);
    setStatus("");
  };

  const loadParticipant = async (id) => {
    resetView();

    try {
      // 1) Load deelnemers and find matching id
      const res = await fetch(`${API_BASE}/deelnemers`);
      if (!res.ok) throw new Error("Kon deelnemers niet laden");
      const deelnemers = await res.json();
      const p = (deelnemers || []).find((x) => Number(x.id) === Number(id));
      if (!p) {
        setStatus(`Geen deelnemer gevonden met id ${id}`);
        // still randomize arrow/score as requested
        setRandomScore(Math.floor(Math.random() * 199) - 99);
        setArrowLeftPct(5 + Math.random() * 90);
        return;
      }

      setParticipant(p);
      if (p.profielfoto) {
        setProfileUrl(`${API_BASE}/uploads/${p.profielfoto}`);
      } else {
        setProfileUrl(null);
      }

      // 2) Live portrait video
      setVideoError(false);
      setVideoSrc(`${API_BASE}/liveportraits/lp--${id}.mp4?t=${Date.now()}`);

      // Load side video (random from /videos)
      try {
        const vres = await fetch(
          `${API_BASE}/videos?deelnemer_id=${encodeURIComponent(id)}`
        );
        if (vres.ok) {
          const vrows = await vres.json();
          if (Array.isArray(vrows) && vrows.length > 0) {
            const pick = vrows[Math.floor(Math.random() * vrows.length)];
            const path = String(pick.storage_path || "").replace(/^\/+/, "");
            setSideVideoSrc(`${API_BASE}/${path}?t=${Date.now()}`);
            setSideVideoError(false);
          } else {
            setSideVideoSrc(null);
          }
        } else {
          setSideVideoSrc(null);
        }
      } catch {
        setSideVideoSrc(null);
      }

      // 3) Load photos in background
      try {
        const pres = await fetch(
          `${API_BASE}/photos?deelnemer_id=${encodeURIComponent(id)}`
        );
        if (pres.ok) {
          const rows = await pres.json();
          console.log("photos", rows);
          const selected = shuffle(rows || []).slice(0, 5);
          const urls = selected.map((r) => {
            // storage_path in DB is like 'uploads/..'; make absolute
            const path = String(r.storage_path || "").replace(/^\/+/, "");
            return `${API_BASE}/${path}`;
          });
          setPhotoUrls(urls);
        } else {
          setPhotoUrls([]);
        }
      } catch {
        setPhotoUrls([]);
      }

      // 4) Random arrow + score
      setRandomScore(Math.floor(Math.random() * 199) - 99);
      setArrowLeftPct(5 + Math.random() * 90);
    } catch (err) {
      setStatus(err.message || "Er trad een fout op");
      // still show random arrow/score
      setRandomScore(Math.floor(Math.random() * 199) - 99);
      setArrowLeftPct(5 + Math.random() * 90);
    }
  };

  useEffect(() => {
    async function fetchScores() {
      if (!participant || !participant.id) {
        setCaptchaScores({ good: 0, missed: 0, wrong: 0 });
        return;
      }
      try {
        const res = await fetch(
          `${API_BASE}/scores?deelnemer_id=${encodeURIComponent(participant.id)}`
        );
        if (!res.ok) {
          setCaptchaScores({ good: 0, missed: 0, wrong: 0 });
          return;
        }
        const rows = await res.json();
        if (Array.isArray(rows) && rows.length > 0) {
          // pick latest by created_at if present, otherwise last entry
          let latest = rows[0];
          if (rows[0].created_at) {
            latest = rows.reduce((a, b) =>
              new Date(a.created_at) > new Date(b.created_at) ? a : b
            );
          } else {
            latest = rows[rows.length - 1];
          }
          setCaptchaScores({
            good: Number(latest.good) || 0,
            missed: Number(latest.missed) || 0,
            wrong: Number(latest.wrong) || 0,
          });
        } else {
          setCaptchaScores({ good: 0, missed: 0, wrong: 0 });
        }
      } catch (err) {
        setCaptchaScores({ good: 0, missed: 0, wrong: 0 });
      }
    }
    fetchScores();
  }, [participant]);

  const nameDisplay = useMemo(() => {
    if (!participant) return "NAAM DEELNEMER";
    if (participant.naam) return participant.naam;
    return `Deelnemer ${participant.id}`;
  }, [participant]);

  // derived chart values (percent + pixel heights for reliable rendering)
  const good = captchaScores.good || 0;
  const missed = captchaScores.missed || 0;
  const wrong = captchaScores.wrong || 0;
  const maxScore = Math.max(good, missed, wrong, 1);
  const heightsPercent = {
    good: (good / maxScore) * 100,
    missed: (missed / maxScore) * 100,
    wrong: (wrong / maxScore) * 100,
  };
  // compute pixel heights based on measured chartInnerHeight; ensure a minimum so bars are visible
  const minPx = 6;
  const heightsPx = {
    good: Math.max(minPx, Math.round((heightsPercent.good / 100) * (chartInnerHeight || 0))),
    missed: Math.max(minPx, Math.round((heightsPercent.missed / 100) * (chartInnerHeight || 0))),
    wrong: Math.max(minPx, Math.round((heightsPercent.wrong / 100) * (chartInnerHeight || 0))),
  };
  // fallback percent heights if measurement not yet available (keeps inline percent present)
  const heights = {
    good: chartInnerHeight ? `${heightsPx.good}px` : `${Math.round(heightsPercent.good)}%`,
    missed: chartInnerHeight ? `${heightsPx.missed}px` : `${Math.round(heightsPercent.missed)}%`,
    wrong: chartInnerHeight ? `${heightsPx.wrong}px` : `${Math.round(heightsPercent.wrong)}%`,
  };

  return (
    <div style={styles.page}>
      {/* Left column */}
      <div style={styles.leftCol}>
        <div>
          <div style={styles.title}>AIAIAI</div>
          <div style={styles.subtitle}>
            Attraction for Interactive AI-anxiety Interpretation
          </div>
        </div>

        <div style={styles.profileBlock}>
          {/* Profile photo frame */}
          <div style={{ ...styles.frame, ...styles.profileFrame }}>
            {profileUrl ? (
              <img src={profileUrl} alt="Profielfoto" style={styles.profileImg} />
            ) : (
              <div style={{ textAlign: "center" }}>PROFIELFOTO</div>
            )}
          </div>

  {/* Inline: name on the left (4fr) and participant id input on the right (1fr) */}
  <div style={styles.nameRow}>
    <div style={styles.nameInline}>{nameDisplay}</div>
    <input
      type="text"
      value={idInput}
      onChange={handleIdChange}
      onKeyDown={handleKeyDown}
      placeholder="000"
      maxLength={3}
      inputMode="numeric"
      pattern="\\d{3}"
      style={styles.idInputInline}
      aria-label="Deelnemer ID"
    />
  </div>

          {/* Logos placeholder */}
          <div style={styles.logosPlaceholder}>
            <img
              src="/images/mindlabs-meets.png"
              alt="MindLabs Meets"
              style={styles.logoMindlabs}
            />
          </div>

          {status ? <div style={styles.smallNote}>{status}</div> : null}
        </div>
      </div>

      {/* Divider */}
      <div style={styles.divider} />

      {/* Right column */}
      <div style={styles.rightCol}>
        {/* Top: photos row */}
        <div style={styles.photosRow}>
          {[0, 1, 2, 3, 4].map((i) => {
            const url = photoUrls[i] || null;
            return (
              <div key={i} style={styles.photoCell}>
                {url ? (
                  <img src={url} alt={`Foto ${i + 1}`} style={styles.photoImg} />
                ) : (
                  <div style={styles.photoPlaceholder}>FOTO {i + 1}</div>
                )}
              </div>
            );
          })}
        </div>

        {/* Middle: Observation (left), Captcha (middle), Future self (right) */}
        <div style={styles.middleArea}>
          {/* Left column - Observation */}
          <div style={styles.middleColWrap}>
            <div style={styles.smallTitle}>Observation</div>
            <div style={styles.bigVideoFrame}>
              {sideVideoSrc && !sideVideoError ? (
                <video
                  src={sideVideoSrc}
                  autoPlay
                  loop
                  playsInline
                  muted
                  style={styles.video}
                  onError={() => setSideVideoError(true)}
                  onCanPlay={() => setSideVideoError(false)}
                />
              ) : (
                <div style={{ textAlign: "center" }}>Observation</div>
              )}
            </div>
          </div>

          {/* Middle column - Captcha bars */}
          <div style={styles.middleColWrap}>
            <div style={styles.smallTitle}>Captcha</div>
            <div style={styles.bigVideoFrame}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "12px",
                  boxSizing: "border-box",
                }}
              >
                <div
                  ref={chartAreaRef}
                  style={{
                    display: "flex",
                    alignItems: "flex-end",
                    height: "70%",
                    gap: "18px",
                    width: "100%",
                    justifyContent: "center",
                  }}
                >
                  {/* Good */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <div
                      style={{
                        width: "36px",
                        height: heights.good,
                        background: "#0aa84f",
                        transition: "height 300ms ease",
                        boxSizing: "border-box",
                        borderRadius: "3px",
                      }}
                    />
                    <div style={{ fontSize: "14px", fontWeight: 600 }}>Good</div>
                    <div style={{ fontSize: "12px" }}>{good}</div>
                  </div>

                  {/* Missed */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <div
                      style={{
                        width: "36px",
                        height: heights.missed,
                        background: "#ff8f3a",
                        transition: "height 300ms ease",
                        boxSizing: "border-box",
                        borderRadius: "3px",
                      }}
                    />
                    <div style={{ fontSize: "14px", fontWeight: 600 }}>Missed</div>
                    <div style={{ fontSize: "12px" }}>{missed}</div>
                  </div>

                  {/* Wrong */}
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <div
                      style={{
                        width: "36px",
                        height: heights.wrong,
                        background: "#f44336",
                        transition: "height 300ms ease",
                        boxSizing: "border-box",
                        borderRadius: "3px",
                      }}
                    />
                    <div style={{ fontSize: "14px", fontWeight: 600 }}>Wrong</div>
                    <div style={{ fontSize: "12px" }}>{wrong}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right column - Future Self */}
          <div style={styles.middleColWrap}>
            <div style={styles.smallTitle}>Future self</div>
            <div style={styles.bigVideoFrame}>
              {videoSrc && !videoError ? (
                <video
                  src={videoSrc}
                  autoPlay
                  loop
                  playsInline
                  muted
                  style={styles.video}
                  onError={() => setVideoError(true)}
                  onCanPlay={() => setVideoError(false)}
                />
              ) : (
                <div style={{ textAlign: "center" }}>Future Self</div>
              )}
            </div>
          </div>
        </div>

        {/* Bottom: rainbow bar + arrow */}
        <div style={styles.bottomBarWrap}>
          <div style={styles.barGroup}>
            {/* Arrow */}
            <div
              style={{
                ...styles.arrowContainer,
                left: `${clamp(arrowLeftPct, 0, 100)}%`,
              }}
            >
              <svg width="128" height="80" viewBox="0 0 64 40">
                <polygon points="0,0 64,0 32,40" fill="#1976d2" />
              </svg>
              <div style={styles.arrowText}>{randomScore}</div>
            </div>

            {/* Rainbow bar */}
            <div style={styles.rainbowBar} />
          </div>
        </div>
      </div>
    </div>
  );
}

export default AIAIAIDashboard;
